// The identity of ONE "the member pressed Create" (Phase 1 / T1.6B3).
//
// A creation attempt gets a `creationId` minted ONCE and resent unchanged on
// every retry of that same attempt. The server makes it the Business's primary
// key, so the DATABASE decides the winner of a double-submit or a race — the
// client never has to.
//
// It is deliberately NOT a timestamp and NOT "the user's latest provisional
// business": two deliberate Add-Business attempts a minute apart are two
// businesses, and any heuristic that collapses them by proximity silently
// loses one. Proximity is not identity.
//
// Stored in sessionStorage so a page reload mid-attempt (or a bounce through
// OAuth) resumes the SAME attempt rather than starting a second one, and so it
// dies with the tab rather than leaking into the next session.

const KEY = 'pb.creationAttempt';

/** Crockford base32, ULID shape — the format the server validates. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function mint(): string {
  const bytes = new Uint8Array(26);
  crypto.getRandomValues(bytes);
  // A ULID's leading characters are a timestamp in the real encoder; here the
  // value only has to be unguessable and correctly shaped, so every character
  // is random. The server treats it as an opaque key, never as a clock.
  return Array.from(bytes, (b) => ALPHABET[b % 32]!).join('');
}

/**
 * The current attempt for `mode`, minting one if there is none.
 *
 * Keyed by mode so that "create my first business" and "add another business"
 * can never share an attempt — the second must be a genuinely new tenant.
 */
export function currentAttempt(mode: string): string {
  try {
    const raw = sessionStorage.getItem(`${KEY}:${mode}`);
    if (raw && /^[0-9A-HJKMNP-TV-Z]{26}$/.test(raw)) return raw;
  } catch {
    // Private mode / blocked storage: fall through and mint. A fresh id per
    // render is worse than a stable one, but it is still correct — the server
    // is the arbiter, and the only cost is that a retry makes a new tenant
    // rather than converging.
  }
  const id = mint();
  try {
    sessionStorage.setItem(`${KEY}:${mode}`, id);
  } catch {
    /* nothing to do — see above */
  }
  return id;
}

/** Called once an attempt has produced a tenant, so the next one is new. */
export function clearAttempt(mode: string): void {
  try {
    sessionStorage.removeItem(`${KEY}:${mode}`);
  } catch {
    /* nothing to do */
  }
}
